import defaultProductImage from "@/shared/assets/default_product_image.png";
import { useCartStore } from "@/cart/stores/cart";
import type { CartItem } from "@/cart/types/cart";
import { resolveKioskProductImage } from "@/kiosk/utils/kioskCatalog.utils";

export const UNCATEGORIZED_PRODUCTS_CLASS_ID = "__UNCATEGORIZED__";

const isUncategorizedCartItem = (item: Partial<CartItem>): boolean =>
  item.productClassId === UNCATEGORIZED_PRODUCTS_CLASS_ID;

type CartQuantityLookupItem = {
  id?: string;
  productId?: string;
  product?: string;
  locationId?: string | null;
};

export const getCartQuantity = (
  item: CartQuantityLookupItem,
  locationId?: string | null,
): number => {
  const cartStore = useCartStore();
  const productId = item.productId || item.product || item.id;
  const resolvedLocationId = locationId ?? item.locationId ?? null;

  // Find the item in the cart
  const cartItems = cartStore.items.filter((i) => {
    if (i.productId !== productId) {
      return false;
    }
    if (typeof resolvedLocationId === "string" && resolvedLocationId) {
      return i.locationId === resolvedLocationId;
    }
    return true;
  });

  return cartItems.reduce((total, item) => total + item.quantity, 0);
};

export const getCartItemProductName = (item: Partial<CartItem>): string => {
  const cartStore = useCartStore();

  return cartStore.products.get(item.productId ?? "")?.name ?? item.productName ?? "";
};

export const getCartItemProductClassName = (item: Partial<CartItem>): string => {
  if (isUncategorizedCartItem(item)) {
    return "";
  }

  const cartStore = useCartStore();

  return (
    cartStore.productClasses.get(item.productClassId ?? "")?.name ?? item.productClassName ?? ""
  );
};

export const getCartItemPrimaryLabel = (item: Partial<CartItem>): string => {
  return (
    getCartItemProductClassName(item) ||
    getCartItemProductName(item) ||
    item.priceName ||
    item.sku ||
    "-"
  );
};

export const getCartItemSecondaryLabel = (item: Partial<CartItem>): string => {
  if (isUncategorizedCartItem(item)) {
    return "";
  }

  return getCartItemProductName(item) || item.priceName || item.sku || "-";
};

export const getCartItemImage = (item: Partial<CartItem>): string => {
  const cartStore = useCartStore();
  const productInfo = cartStore.products.get(item.productId ?? "");
  const productClassInfo = cartStore.productClasses.get(item.productClassId ?? "");
  const hasLiveProductImages = Array.isArray(productInfo?.images) && productInfo.images.length > 0;
  const hasLiveProductClassImages =
    Array.isArray(productClassInfo?.images) && productClassInfo.images.length > 0;

  return (
    resolveKioskProductImage(
      hasLiveProductImages
        ? { images: productInfo.images }
        : {
            images: Array.isArray(item.productImages)
              ? item.productImages
              : item.image
                ? [item.image]
                : [],
          },
      hasLiveProductClassImages
        ? { images: productClassInfo.images }
        : {
            images: Array.isArray(item.productClassImages) ? item.productClassImages : [],
          },
    ) ?? defaultProductImage
  );
};
